/*
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 */
import React, {
  useEffect,
  useState
} from 'react';

import {
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import {
  FontAwesomeIcon
} from '@fortawesome/react-fontawesome';
import {
  Button,
  Pagination,
  TableProps,
  Tooltip,
  Typography
} from 'antd';

import copy from 'copy-to-clipboard';

import _isFinite from 'lodash/isFinite';

import OlFeature from 'ol/Feature';
import OlFormatGeoJSON from 'ol/format/GeoJSON';
import OlLayerVector from 'ol/layer/Vector';
import OlSourceVector from 'ol/source/Vector';
import OlStyleCircle from 'ol/style/Circle';
import OlStyleFill from 'ol/style/Fill';
import OlStyleStroke from 'ol/style/Stroke';
import OlStyle from 'ol/style/Style';

import {
  useTranslation
} from 'react-i18next';

import MapUtil from '@terrestris/ol-util/dist/MapUtil/MapUtil';

import {
  SimpleButton,
  useMap
} from '@terrestris/react-geo';
import PropertyGrid from '@terrestris/react-geo/dist/Grid/PropertyGrid/PropertyGrid';

import {
  useAppSelector
} from '../../../hooks/useAppSelector';

import './index.less';

export type FeatureInfoPropertyGridProps = {
  features: OlFeature[];
  layerName?: string;
} & TableProps<OlFeature>;

const { Title } = Typography;

export const FeatureInfoPropertyGrid: React.FC<FeatureInfoPropertyGridProps> = ({
  features,
  layerName = 'Unknown layer',
  ...restProps
}): JSX.Element => {
  const selectedTopic = useAppSelector((state) => state.layer.selectedTopic);

  const [currentPage, setCurrentPage] = useState<number>();
  const [selectedFeature, setSelectedFeature] = useState<OlFeature>();

  const map = useMap();

  const {
    t
  } = useTranslation();

  const vectorLayerName = `selection-layer-${layerName}`;

  const onChange = (page: number) => {
    setCurrentPage(page);
    setSelectedFeature(features[page - 1]);
  };

  const getAttributeNames = (): any => {
    switch (layerName) {
      case 'lk_oldenburg_pv':
        return {
          'modanetto': 'Geeignete Modulfläche in m²',
          'radabs': 'nutzbare Einstrahlung in kWh/m²*Jahr',
          'kw': 'max. Leistung in kWp',
          'str': 'potenzieller Stromerstrag in kWh/Jahr',
          'himmel_kat': 'Himmelsrichtung'
        };
      case 'lk_oldenburg_th':
        return {
          'modarea': 'geeignete Kollektorfläche in m²',
          'radabs': 'nutzbare Einstrahlung in kWh/m²* Jahr',
          'waerme_kwh': 'potenzieller Wärmeertrag in kWh/Jahr'
        };
      case 'lk_oldenburg_gruen':
        return {
          'modarea': 'Dachfläche in m²',
          'neigung': 'Dachneigung in Grad (°)',
          'ret_20': 'Retentionsleistung in l/h',
          'staubbin': 'Staubbindung in g/a',
          'co2_20': 'CO2-Bindung in kg/a'
        };
      default:
        return {};
    }
  };

  const openCalculationLink = () => {
    const baseUrl= 'https://solar-lk-oldenburg.ipsyscon.de/ertragsrechner/startseite?'; // todo: make configurable
    let link;
    let daus;
    let dfla;
    let dnei;
    let gstr;
    let kies;
    switch (layerName) {
      case 'lk_oldenburg_pv':
        daus = selectedFeature?.get('richtung');
        dfla = selectedFeature?.get('modanetto');
        dnei = selectedFeature?.get('neigung');
        gstr = selectedFeature?.get('radabs');
        const gebIds = selectedFeature?.get('mod_id');
        link = `${baseUrl}method=pv&daus=${daus}&dfla=${dfla}&dnei=${dnei}&gstr_jahr=${gstr}&gebids=${gebIds}`;
        break;
      case 'lk_oldenburg_th':
        dfla = selectedFeature?.get('modarea');
        gstr = getGstr();
        link = `${baseUrl}method=st&dfla=${dfla}&gstr=${gstr}`;
        break;
      case 'lk_oldenburg_gruen':
        dfla = selectedFeature?.get('modarea');
        dnei = selectedFeature?.get('neigung');
        gstr = selectedFeature?.get('yr_mean');
        kies = selectedFeature?.get('modarea_ki');
        link = `${baseUrl}method=gruendach&dfla=${dfla}&dnei=${dnei}&gstr_jahr=${gstr}&dfla_kies=${kies}`;
        break;
      default:
        break;
    }
    if (link) {
      window.open(link);
    }
  };

  const getGstr = (): string => {
    const jan = selectedFeature?.get('jan_median');
    const feb = selectedFeature?.get('feb_median');
    const mrz = selectedFeature?.get('mrz_median');
    const apr = selectedFeature?.get('apr_median');
    const mai = selectedFeature?.get('mai_median');
    const jun = selectedFeature?.get('jun_median');
    const jul = selectedFeature?.get('jul_median');
    const aug = selectedFeature?.get('aug_median');
    const sep = selectedFeature?.get('sep_median');
    const okt = selectedFeature?.get('okt_median');
    const nov = selectedFeature?.get('nov_median');
    const dez = selectedFeature?.get('dez_median');
    return `${jan};${feb};${mrz};${apr};${mai};${jun};${jul};${aug};${sep};${okt};${nov};${dez}`;
  };

  /**
   * Reset selected feature when selected topic changes.
   */
  useEffect(() => {
    setSelectedFeature(undefined);
  }, [selectedTopic]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const initVectorLayer = () => {
      if (MapUtil.getLayerByName(map, vectorLayerName)) {
        return;
      }

      const source = new OlSourceVector({
        features: features
      });

      const fill = new OlStyleFill({
        color: 'rgba(255, 255, 255, 0.15)'
      });
      const stroke = new OlStyleStroke({
        color: 'rgba(209, 70, 47, 1)',
        width: 2
      });
      const featureStyle = new OlStyle({
        fill,
        stroke,
        image: new OlStyleCircle({
          radius: 5,
          fill,
          stroke
        })
      });

      const layer = new OlLayerVector({
        source: source,
        style: featureStyle
      });

      layer.set('name', vectorLayerName);

      map.addLayer(layer);
    };

    const deinitVectorLayer = () => {
      const vectorLayer = MapUtil.getLayerByName(map, vectorLayerName) as OlLayerVector<OlSourceVector>;

      if (!vectorLayer) {
        return;
      }

      map.removeLayer(vectorLayer);
    };

    initVectorLayer();

    return () => deinitVectorLayer();
  }, [map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const vectorLayer = MapUtil.getLayerByName(map, vectorLayerName) as OlLayerVector<OlSourceVector>;

    if (!vectorLayer) {
      return;
    }

    vectorLayer.getSource()?.clear();
    setCurrentPage(1);

    if (features.length > 0) {
      setSelectedFeature(features[0]);
    }
  }, [features, map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const vectorLayer = MapUtil.getLayerByName(map, vectorLayerName) as OlLayerVector<OlSourceVector>;

    if (!vectorLayer) {
      return;
    }

    vectorLayer.getSource()?.clear();

    if (selectedFeature) {
      vectorLayer.getSource()?.addFeature(selectedFeature);
    }
  }, [selectedFeature, map]);

  if (!selectedFeature) {
    return <></>;
  }

  return (
    <div className='project-feature-property-grid'>
      <div
        className='property-grid-header'
      >
        {
          layerName && <span><Title level={5}>{t(`FeaturePropertyGrid.${layerName}`)}</Title></span>
        }
        <div
          className='right-elements'
        >
          <Pagination
            simple
            total={features.length}
            size="small"
            pageSize={1}
            current={currentPage}
            onChange={onChange}
          />
          <Tooltip
            title="In Zwischenablage kopieren"
          >
            <Button
              type="primary"
              onClick={() => {
                copy(new OlFormatGeoJSON().writeFeature(selectedFeature));
              }}
              icon={<FontAwesomeIcon icon={faCopy} />}
            />
          </Tooltip>
        </div>
      </div>
      <PropertyGrid
        feature={selectedFeature}
        attributeFilter={Object.keys(getAttributeNames())}
        attributeNames={getAttributeNames()}
        size="small"
        sticky={true}
        columns={[{
          title: 'Attribut',
          dataIndex: 'attributeName',
          key: 'attributeName',
          width: '75%',
          ellipsis: true,
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.key.localeCompare(b.key)
        }, {
          title: 'Wert',
          dataIndex: 'attributeValue',
          key: 'attributeValue',
          width: '25%',
          ellipsis: true
        }]}
        scroll={{
          y: Object.keys(selectedFeature.getProperties()).length > 6 ?
            250 :
            undefined
        }}
        {...restProps}
      />
      <SimpleButton
        className="calculate-button"
        onClick={openCalculationLink}
      >
        Ertragsrechner starten
      </SimpleButton>
    </div>
  );
};

export default FeatureInfoPropertyGrid;
